/**
 * @module msysApplication
 * @description
 * msysApplication library performs start of Mosy application.
 * It exports start function which invokes application’s beforeStart
 * function and all defined listeners.
 * For mServer, it also setup mCrypt worker and connects to database.
 * Start of application is always invooked like this:
 *   require("msysApplication")(LISTENERS, beforeStart)
 * where LISTENERS is array of listener definitions and
 * beforeStart is function that shall be invoked before start
 * of listeners - usually used to overwrite default listener configurations.
 */
console.logger_worker.debug('system', 'LISTENERS REQUIRED')

if(typeof global ==='object') global.MOSY_APP_WITH_VERSION = MOSY_APP_WITH_VERSION
if(typeof window ==='object') window.MOSY_APP_WITH_VERSION = MOSY_APP_WITH_VERSION

require('msysCommon')
console.logger_worker.debug('system', 'msysCommon REQUIRED')
require('mele')
console.logger_worker.debug('system', 'mele REQUIRED')
const msysFS = require('msysFS')
const { findLastMessage } = require('../msysMessage/file')
const msvrWorkflow = require('msvrWorkflow')

global.MOSY_CR_PREFIX = '59.Zg$4'; // Config prefix
const mcrypt = require('msysCrypt');
const { ConfigCrypto, CryptoEngine, Logger } = require('@soitron/json-value-crypto');

if(/^m\w*Server/.test(MOSY_APP)) console.logger_worker.debug('system', 'msvrWorkflow REQUIRED')

module.exports = start

let configurations = null

function start(listeners, beforeStart, config_from_mm) {
  if(typeof window==='object' && window?.parent) {
    window.parent.postMessage({ type: 'mosy:start:'+MOSY_APP_WITH_VERSION }, '*')
  }

  if(MOSY_APP==='mServer') {
    console.logger_worker.debug('system', 'STARTING MCRYPT')
    if(!mcrypt.setupBackendWorker()) {
      // special handling if we are crypt sub worker, clean up listeners and skip further work
      listeners.length=0
      return
    }
    console.logger_worker.debug('system', 'MCRYPT INITIALIZED')
  }

  if(typeof document==='object') {
    // in GUI applications, wait until device is ready
    document.addEventListener('deviceready', ()=>{
      startWhenReady(listeners, beforeStart, config_from_mm)
    }, false)
  }else{
    // in Node applications, start immediately
    startWhenReady(listeners, beforeStart, config_from_mm)
  }
} // start

async function startWhenReady(listeners, beforeStart, config_from_mm) {
  console.logger_worker.debug('system', 'DEVICE READY')

  const start = (config)=>{
    if(configurations.errors?.length > 0 && config?.exit_on_invalid_cfg) {
      // a broken configuration file means the application would silently run without it;
      // applications opt into refusing to start via exit_on_invalid_cfg (beforeStart or a valid cfg file)
      return abortStart('Invalid configuration file(s), refusing to start:\n'+configurations.errors.join('\n'))
    }
    if(MOSY_APP==='mServer' || MOSY_APP==='mIntegration') {
      require('msysDB').init(config.msysDB, '', ()=>startListeners(config, listeners))
    }else{
      startListeners(config, listeners)
    }
  }

  let cfg
  if(config_from_mm) {
    const { configuration: mm_cfg, checksum } = await readConfigurationFromMM(config_from_mm)
    configurations = { mm_cfg, checksum }
    cfg = structuredClone(mm_cfg)
    console.logger_worker.debug('system', 'CONFIGURATION LOADED FROM MM')
  }else {
    configurations = await msysFS.promises.readConfiguration()
    cfg = structuredClone(configurations.file_cfg)

    if (cfg?.crp) {
      // crp >> CRypto Pass >> heslo na decrypt-ovanie konfigurácie
      cfg = decodeJsonConfig(cfg, cfg?.crp);
    }

    console.logger_worker.debug('system', 'CONFIGURATION LOADED FROM CFG FILES')
  }

  if(beforeStart && beforeStart.length===2) {
    beforeStart(cfg, ()=>{
      configurations.app_cfg = cfg
      start(cfg)
    })
  }else{
    if(beforeStart) beforeStart(cfg)
    configurations.app_cfg = cfg
    start(cfg)
  }
} // startWhenReady

/**
 * @param {Object} configObject
 * @param {string} password
 * @return {Object}
 */
function decodeJsonConfig(configObject, password) {
  Logger.setLogger(console);
  CryptoEngine.setPepper(mcrypt.pprefix('MOSY_CR_PREFIX'));
  if (!password) {
    Logger.warn('MOSY_CONFIG_DECRYPTION', 'Configuration is not decrypted. Password is not provided.');
    return configObject;
  }
  try {
    Logger.info('MOSY_CONFIG_DECRYPTION', 'Configuration was decrypted successfully.');
    const crypto = new ConfigCrypto({ password });
    return crypto.decryptObject(configObject);
  } catch (e) {
    Logger.error('MOSY_CONFIG_DECRYPTION', `Configuration is not decrypted. ${e.message}`);
    return configObject;
  }
}

function startListeners(config, listeners) {
  console.logger_worker.debug('system', 'BEFORELOAD EXECUTED')

  // apply global configurations for custom elements
  for(const item in config) {
    if(!/^mele_/.test(item)) continue
    const cfg = config[item]
    if(!cfg || typeof cfg!=='object') continue
    const mele = customElements.get(item.replace(/_/g, '-'))
    if(!mele) continue
    if(!mele._properties || typeof mele._properties!=='object') continue
    for(const prop in cfg) {
      const new_default = cfg[prop]
      const def = mele._properties[prop]
      if(!def || typeof def!=='object') continue
      if(typeof new_default==='undefined' || new_default===null) {
        delete def.default
      }else {
        def.default = new_default
      }
    }
  }

  // store the configuration for later use
  module.exports.config = config

  // invoke non-disabled listeners
  console.logger_worker.debug('system', 'CONSTRUCTING LISTENERS')
  for(let listener of listeners) {
    if(!listener) continue

    // special handling for workflow definitions
    if(typeof listener==='function') {
      if(!msvrWorkflow) continue // msvrWorkflow is not available in non-server applications

      const def = {}
      let listener_id
      const webpacked_files = listener.keys()
      for(const file of webpacked_files) {
        const m = /[\\/]wf(\w+)\.(\w+)$/.exec(file) // we expect file name to be wfXXX.xxx
        const ext = m?.[2] ?? ''
        if(ext==='json') {
          def[ext] = listener(file)
          listener_id = m[1] // workflow name is derived from .json file
        }else{
          def[ext] = listener(file)
        }
      }

      if(!listener_id) continue

      // construct workflow configuration
      listener_id = 'msvrWf' + listener_id
      config[listener_id] = _.defaultsDeep(
        config[listener_id],
        def.json,
        {
          translations: def.csv || '',
          scripts: def.js || '',
          styles: def.css || '',
        },
      )

      // construct workflow listener definition as instance of msvrWorkflow with custom listener ID
      listener = [msvrWorkflow, listener_id]
    }

    // prepare listener's configuration
    let [class_ref, listener_id, cfg_default] = Array.isArray(listener) ? listener : [listener]
    if(!listener_id) {
      listener_id = class_ref.default.prototype.constructor.name
    }
    const cfg = config[listener_id] || {}
    _.defaultsDeep(cfg, cfg_default)

    // construct the listener object from class
    try{
      // eslint-disable-next-line no-new
      const listener = new class_ref.default(listener_id, cfg)
      listener._configurations = configurations
    }catch(e) {
      console.error(e)
    }
  }

  console.logger_worker.debug('system', 'STARTING LISTENERS')
} // startListeners

async function abortStart(err) {
  console.error_orig(err)
  await asyncTimeout(100)
  if(typeof process === 'object' && process?.exit) process.exit(1)
  throw new Error(err) // browser context has no process to exit; stop the startup chain instead
} // abortStart

async function readConfigurationFromMM(config_from_mm) {
  if(!config_from_mm || typeof config_from_mm!=='object') {
    return abortStart('Missing mm configuration')
  }
  let root = config_from_mm?.msvcWriter?.root
  root = root ? msysFS.fullpath(root) : undefined
  if(!root) {
    return abortStart(`Missing root in mm configuration: config_from_mm.configuration.${MOSY_APP}.msvcWriter.root`)
  }
  const index = 'appconfig'
  const action = 'mmAppConfiguration'
  const max_days = 10 * 365 // 10 years
  const msg = await findLastMessage(root, index, action, max_days)
  const configuration = msg?.params?.configuration
  if(configuration && typeof configuration==='object') {
    const { event_dt } = msg.params
    // checksum may be absent in older mmAppConfiguration records; treat as empty for backwards compatibility
    const checksum = msg?.params?.checksum || ''
    const { profile, version, configurator } = configuration
    console.logger_worker.debug('system', `CONFIGURATION ${profile} v ${version}${configurator ? ' by ' + configurator : ''} loaded from time ${_.usec2dtus(event_dt)} (${event_dt})`)
    return { configuration, checksum }
  }else {
    const { profile, version, configurator } = config_from_mm
    console.logger_worker.warn('system', `BUILT-IN CONFIGURATION ${profile} v ${version}${configurator ? ' by ' + configurator : ''} is used`)
    return { configuration: config_from_mm, checksum: '' }
  }
} // readConfigurationFromMM
